import React, { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import {
    Badge,
    Button,
    Input,
    RegionState,
    Select,
    SelectOption,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';
import { MagnifyingGlass } from '@freelancelabs/icons';
import { SORT_DIRECTION } from '@freelancelabs/shared';

import ListCard, { WRAPPING_HEADER } from '../../components/ListCard';
import { SortDirection } from '../../graphql/__generated__/schema';
import { AccountsDocument, PortalModulesDocument } from '../../graphql/__generated__/types';
import { accountsRefusalOf } from './accountsRefusal';

const PAGE_SIZE = 25;

/** Long enough not to query on every keystroke, short enough not to feel laggy. */
const SEARCH_DEBOUNCE_MS = 300;

/** Open, closed, or both. `ALL` is the absence of the argument, and travels as null. */
type PortalFilter = 'ALL' | 'OPEN' | 'CLOSED';

/**
 * The accounts list of the back-office (PCG-141).
 *
 * **Why the screen exists.** Opening an account to the portal, activating its modules
 * and declaring a dashboard were all reached by going to the **users list** and
 * filtering it on one account, which is where PCG-79 put them for want of a « Comptes »
 * module. That was a home, not an address: nobody looks for an account under
 * « Utilisateurs », and the three panels each carried a comment saying they were
 * waiting for this screen. They now live on the account sheet, and the users list
 * filters users and nothing else.
 *
 * **A flat table, and the volume set cannot judge that decision.** The parc holds
 * 6 975 roots for 7 584 accounts, so a foldable tree would render almost as many root
 * nodes as accounts, each with one child: a flat list dressed as a tree. The volume
 * set holds 900 roots for 7 583 accounts and would make the tree look reasonable. So
 * the company is a **column**, and the attention goes to the search.
 *
 * **Three counters, and each one is read rather than computed.** The people count is
 * `userCount`, which counts **people** and not memberships. This repository has
 * already announced 10 666 users for 8 000 people, and the front recounting anything
 * here is how that comes back. The modules count is a **numerator with no
 * denominator**: the « sur 6 » is read on `portalModules`, the referential that names
 * the whole menu, never written as a constant. The dashboards count is zero on almost
 * every account, which is the ordinary state of a catalogue nobody has filled.
 *
 * **The internal organisation is not in this list**, deliberately, while it stays in
 * the actor's perimeter: every gesture this list leads to refuses on Freelance.com
 * with `INTERNAL_ORGANIZATION_NOT_ALLOWED`, and a row on which the whole screen
 * refuses only misleads. The page says « comptes clients » rather than warning about
 * an absence nobody would otherwise notice.
 */
const AccountsPage = () => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [portal, setPortal] = useState<PortalFilter>('ALL');
    const [direction, setDirection] = useState<SortDirection>(SortDirection.Asc);
    const [page, setPage] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    // Any change of criteria sends us back to the first page: staying on page 3 of a
    // result set that now holds four rows shows an empty table and reads as a bug.
    useEffect(() => setPage(0), [debouncedSearch, portal, direction]);

    /**
     * **`cache-and-network`, and it is the price of moving the panels off this list.**
     * All four facts of a row (the portal access and the three counters) are changed
     * from the **account sheet**, which is another screen. Coming back through the
     * router does not refetch, so a cache-first list would show the account still
     * closed right after it was opened, and the operator would open it twice.
     *
     * The alternative was to add this document to the `refetchQueries` of six dialogs,
     * which refetch only **active** queries: the list is unmounted while the sheet is
     * open, so every one of them would have been a no-op. One policy here beats six
     * couplings that do not work.
     */
    const { data, loading, error } = useQuery(AccountsDocument, {
        variables: {
            // Null and not omitted: the absence of the argument is what asks for both,
            // and it has to travel as such.
            search: debouncedSearch || null,
            portalEnabled: portal === 'ALL' ? null : portal === 'OPEN',
            direction,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
        },
        fetchPolicy: 'cache-and-network',
    });

    /**
     * The denominator of the modules column, and the one place it may be read.
     * `portalModules` names the whole portal menu and grows with it; « sur 6 » written
     * here would be a wrong number the day a seventh module is seeded.
     */
    const { data: modulesData } = useQuery(PortalModulesDocument);
    const moduleTotal = modulesData?.portalModules?.length ?? null;

    const result = data?.accounts;
    const items = result?.items ?? [];
    // Never `items.length`: that counts a page. The API's total follows the search and
    // the filter, so it counts exactly what the reader is paging through.
    const total = result?.total ?? 0;
    const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
    // ttag refuses an expression inside a localized template: the two numbers are
    // computed here so the string holds nothing but placeholders.
    const currentPageNumber = page + 1;
    const pageCount = lastPage + 1;

    const filtered = Boolean(debouncedSearch) || portal !== 'ALL';

    /**
     * The skeleton belongs to « nothing to show yet », never to « a request is in
     * flight ». Under `cache-and-network` the two are not the same: coming back to a
     * page already in cache renders it at once and refreshes underneath, and keying the
     * skeleton on `loading` would blank a table the reader is already reading. Keyed on
     * the absence of data, it still covers what it must: a new search or an unread page
     * has no cache entry, so `data` is undefined and the skeleton shows rather than the
     * empty state, which would otherwise flash « aucun compte » on every keystroke.
     */
    const showSkeleton = loading && !data;

    /** Read once: it is a `switch` plus three translated strings, not a getter. */
    const refusal = error ? accountsRefusalOf(error) : null;

    const clearFilters = () => {
        setSearch('');
        setDebouncedSearch('');
        setPortal('ALL');
    };

    const portalOptions: SelectOption<PortalFilter>[] = [
        { label: c('Accounts filter').t`Open and closed`, value: 'ALL' },
        { label: c('Accounts filter').t`Open to the portal`, value: 'OPEN' },
        { label: c('Accounts filter').t`Closed to the portal`, value: 'CLOSED' },
    ];

    /**
     * The name column, and the only sortable one. **There is no `sort` argument on
     * `accounts`, and it is measured rather than forgotten**: ordering by a counter
     * puts its sub-query back below the sort and evaluates it over the whole perimeter
     * 76,8 ms and 7 583 evaluations, against 11 ms and 25 for the alphabetical order.
     * The lot that wants it will decide on that figure.
     */
    const columns: TableHeaderCellProps[] = (
        [
            {
                id: 'name',
                label: c('Accounts column').t`Account`,
                minWidth: 260,
                direction: direction === SortDirection.Asc ? SORT_DIRECTION.ASC : SORT_DIRECTION.DSC,
                onSort: (next: SORT_DIRECTION) =>
                    setDirection(next === SORT_DIRECTION.ASC ? SortDirection.Asc : SortDirection.Desc),
            },
            {
                id: 'company',
                label: c('Accounts column').t`Head office`,
                minWidth: 220,
                // The root is read, never linked: it may be absent from the page and
                // even from the actor's perimeter, so a link would lead nowhere on the
                // rows where it matters most. Said on the header rather than on every
                // row, and it also names what an empty cell would otherwise hide.
                textHelp: c('Accounts column')
                    .t`The client the account belongs to, at the top of its tree. It is shown to be read, not to be opened: it may sit outside your portfolio. An account with no parent is the top of its own tree, and says so.`,
            },
            { id: 'portal', label: c('Accounts column').t`Portal access`, minWidth: 130 },
            {
                id: 'users',
                label: c('Accounts column').t`Users`,
                minWidth: 90,
                textAlign: 'end',
                // The sentence PCG-90 had to buy. Said where somebody doubting the
                // figure looks, and it is also what tells them why it disagrees with a
                // count of memberships taken elsewhere.
                textHelp: c('Accounts column')
                    .t`People holding a live access on this account, invited ones included. Revoked and departed accesses are not counted: they are the history of an access, not an access.`,
            },
            {
                id: 'modules',
                label: c('Accounts column').t`Modules`,
                minWidth: 100,
                textAlign: 'end',
                textHelp: c('Accounts column')
                    .t`Modules open on this account, out of the number of entries the portal menu names. That second number is read on the module referential, never written here, and it grows with the menu.`,
            },
            {
                id: 'dashboards',
                label: c('Accounts column').t`Dashboards`,
                minWidth: 110,
                textAlign: 'end',
                textHelp: c('Accounts column')
                    .t`Live entries of the account's dashboard catalogue. Zero is the ordinary state and not a fault: this content is typed in by hand, not ingested.`,
            },
            { id: 'actions', label: '', minWidth: 150, textAlign: 'end' },
        ] as TableHeaderCellProps[]
    ).map((column) => ({ ...column, className: WRAPPING_HEADER }));

    /**
     * The design system's cells hold one line and cut with an ellipsis; an account
     * named « GMD Direction des Systèmes d'Information » is unreadable cut at a third
     * of its length. `!` because `@freelancelabs/styles` is imported after Tailwind in
     * `index.scss`, so at equal specificity the design system would win.
     */
    const wrapping = '!whitespace-normal !break-words py-2';

    /**
     * The company cell. Three cases and not one, because two of them would otherwise
     * render as a bare name that says something false.
     *
     * On the real parc **92 % of accounts are their own root**, so printing the name
     * again would repeat the account's own name on nearly every row and say nothing.
     * The identity is settled on the identifier and never on the name: three accounts
     * are called « ELECTRICITE DE FRANCE » in the dev database.
     *
     * A **null root** is not « no company »: the API says the walk up
     * `parent_organization_id` did not reach a root, which signals a loop. Rendered
     * blank it would read as a missing field.
     */
    const companyCell = (account: {
        id?: string | null;
        rootOrganizationId?: string | null;
        rootOrganizationName?: string | null;
    }) => {
        if (account.rootOrganizationId && account.rootOrganizationId === account.id) {
            return <span className="text-text-500">{c('Accounts list').t`This account`}</span>;
        }
        if (!account.rootOrganizationName) {
            return <span className="text-text-500">{c('Accounts list').t`Company not resolved`}</span>;
        }
        return account.rootOrganizationName;
    };

    /** A counter the API did not send. Blank in a column of figures would read as zero. */
    const countCell = (value: number | null | undefined) =>
        value === null || value === undefined ? (
            <span className="font-sans text-text-500">{c('Accounts list').t`Not counted`}</span>
        ) : (
            value
        );

    return (
        <section className="flex flex-col gap-5">
            <header>
                <h1 className="text-xl font-semibold text-text-900">{c('Accounts').t`Accounts`}</h1>
                {/* « comptes clients » is what says, without a warning about an absence,
                    that Freelance.com is not in this list: it is not a client account,
                    and every gesture the sheet offers refuses on it. */}
                <p className="mt-1 max-w-2xl text-xs text-text-600">
                    {c('Accounts')
                        .t`The client accounts of your portfolio, and how each one is configured for the portal.`}
                </p>
            </header>

            <ListCard
                label={c('Accounts').t`Accounts list`}
                toolbar={
                    <div className="flex flex-wrap items-center gap-3">
                        <Input
                            value={search}
                            onValue={setSearch}
                            clearable
                            prefix={<MagnifyingGlass size={16} />}
                            placeholder={c('Accounts filter').t`Account name or SIREN`}
                            aria-label={c('Accounts filter').t`Search an account`}
                            className="max-w-sm"
                        />
                        <Select
                            options={portalOptions}
                            value={portalOptions.find((option) => option.value === portal)}
                            onChange={(option) => setPortal(option.value)}
                            aria-label={c('Accounts filter').t`Filter on the portal access`}
                            className="max-w-xs"
                        />
                        {filtered && (
                            <Button category="weak" size="small" onClick={clearFilters}>
                                {c('Accounts').t`Clear filters`}
                            </Button>
                        )}
                    </div>
                }
                footer={
                    !showSkeleton &&
                    !error &&
                    items.length > 0 && (
                        <>
                            {/* « correspondant » lives inside the plural rather than glued
                                to it: a fragment concatenated outside `ngettext` cannot
                                agree with the count, and French adjectives do. */}
                            <span>
                                {filtered
                                    ? c('Accounts list').ngettext(
                                          msgid`${total} matching account`,
                                          `${total} matching accounts`,
                                          total
                                      )
                                    : c('Accounts list').ngettext(msgid`${total} account`, `${total} accounts`, total)}
                            </span>
                            {total > PAGE_SIZE && (
                                <nav
                                    className="ml-auto flex items-center gap-3"
                                    aria-label={c('Accounts').t`Pagination`}
                                >
                                    <Button
                                        category="weak"
                                        size="small"
                                        disabled={page === 0}
                                        onClick={() => setPage((current) => current - 1)}
                                    >
                                        {c('Action').t`Previous`}
                                    </Button>
                                    <span className="font-mono text-xs">
                                        {c('Pagination').t`Page ${currentPageNumber} of ${pageCount}`}
                                    </span>
                                    <Button
                                        category="weak"
                                        size="small"
                                        disabled={page >= lastPage}
                                        onClick={() => setPage((current) => current + 1)}
                                    >
                                        {c('Action').t`Next`}
                                    </Button>
                                </nav>
                            )}
                        </>
                    )
                }
            >
                <>
                    <Table>
                        <TableHeader resizable cells={columns} />
                        <TableBody loading={showSkeleton} loadingRows={5} loadingColumns={columns.length}>
                            {(error ? [] : items).map((account) => (
                                <TableRow key={account.id}>
                                    <TableCell className={wrapping}>
                                        {/* The weight is on the text, not on the cell: the
                                            card sets every cell to normal through a rule of
                                            two classes, which a single class cannot outweigh
                                            even with `!`. */}
                                        <span className="font-semibold">{account.name}</span>
                                    </TableCell>
                                    <TableCell className={wrapping}>{companyCell(account)}</TableCell>
                                    <TableCell>
                                        {/* Null is not « fermé ». A missing field rendered as
                                            closed would announce a state nobody set. */}
                                        {account.portalEnabled === null || account.portalEnabled === undefined ? (
                                            <span className="text-sm text-text-500">
                                                {c('Accounts list').t`Not stated`}
                                            </span>
                                        ) : (
                                            <Badge
                                                category={account.portalEnabled ? 'success' : 'default'}
                                                size="small"
                                            >
                                                {account.portalEnabled
                                                    ? c('Portal access').t`Open to the portal`
                                                    : c('Portal access').t`Closed to the portal`}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-end font-mono text-xs">
                                        {countCell(account.userCount)}
                                    </TableCell>
                                    <TableCell className="text-end font-mono text-xs">
                                        {countCell(account.moduleCount)}
                                        {/* The denominator only once the referential has
                                            answered. « 2 / 0 » while it is in flight would be
                                            a false fraction, and the numerator alone stays
                                            true in both moments. */}
                                        {moduleTotal !== null &&
                                            account.moduleCount !== null &&
                                            account.moduleCount !== undefined && (
                                                <span className="text-text-500"> / {moduleTotal}</span>
                                            )}
                                    </TableCell>
                                    <TableCell className="text-end font-mono text-xs">
                                        {countCell(account.dashboardCount)}
                                    </TableCell>
                                    <TableCell className="text-end">
                                        <Button to={`/comptes/${account.id}`} category="weak" size="small">
                                            {c('Accounts').t`More details`}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {/* Three states and not two. An error rendered as an absence would send
                        the operator away sure their portfolio is empty, and an empty
                        portfolio is exactly what the API answers with an empty page rather
                        than with a refusal, so the two have to be told apart here. */}
                    {refusal && (
                        <RegionState
                            tone="error"
                            title={refusal.title}
                            description={
                                <>
                                    {refusal.guidance}
                                    {/* Kept on the generic branch only, where it is the
                                        one thing the support desk can act on: under a
                                        refusal the screen already named, a server log
                                        line reads as a disagreement between the two. */}
                                    {refusal.detail && (
                                        <span className="mt-2 block break-words rounded-md border border-border-norm bg-background-weak p-2 font-mono text-xs">
                                            {refusal.detail}
                                        </span>
                                    )}
                                </>
                            }
                        />
                    )}

                    {!showSkeleton && !error && items.length === 0 && filtered && (
                        <RegionState
                            title={c('Accounts').t`No account matches these criteria.`}
                            // Attention, the SIREN is filled on 5 898 of the 7 583 accounts of the
                            // parc. « Ce compte n'existe pas » would therefore be a wrong
                            // conclusion drawn confidently, on nearly a quarter of them. And
                            // the search deliberately does not read the company's name: the
                            // root's name is not a column, resolving it would mean walking
                            // the tree once per candidate row.
                            description={c('Accounts')
                                .t`The search reads the account name and its SIREN, which is missing on about a fifth of the parc, so an account can be there without matching. It does not read the name of the company: search for the subsidiary rather than for the group.`}
                        >
                            <Button category="weak" size="small" onClick={clearFilters}>
                                {c('Accounts').t`Clear filters`}
                            </Button>
                        </RegionState>
                    )}

                    {!showSkeleton && !error && items.length === 0 && !filtered && (
                        <RegionState
                            title={c('Accounts').t`No account in your portfolio.`}
                            // An empty portfolio is answered with an empty page and never
                            // with a refusal, so this is the ordinary shape of an operator
                            // whose `organization_assignment` has not been filled. Worded as
                            // provisioning rather than as a right that is missing: they hold
                            // the permission, which is how they reached this screen at all.
                            description={c('Accounts')
                                .t`Nothing was refused: the accounts you may act on are simply none for now. An administrator assigns them, account by account, unless your role sees them all.`}
                        />
                    )}
                </>
            </ListCard>
        </section>
    );
};

export default AccountsPage;
